import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTemplates, Template } from '../../contexts/TemplateContext';
import { formatDistanceToNow, format, subDays, subMonths, subYears } from 'date-fns';

const TemplateManagement = () => {
  const navigate = useNavigate();
  const { templates, deleteTemplate, updateTemplate } = useTemplates();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [smsCount, setSmsCount] = useState(1);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  
  // Performance filter states
  const [performanceFilter, setPerformanceFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  // Filter templates based on selected category and search term
  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         template.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Calculate performance metrics based on filter
  const getFilteredTemplates = () => {
    if (performanceFilter === 'all') {
      return templates;
    }
    
    const now = new Date();
    let startDateObj: Date;
    
    switch (performanceFilter) {
      case 'today':
        startDateObj = new Date(now.setHours(0, 0, 0, 0));
        break;
      case 'week':
        startDateObj = subDays(now, 7);
        break;
      case 'month':
        startDateObj = subMonths(now, 1);
        break;
      case 'year':
        startDateObj = subYears(now, 1);
        break;
      case 'custom':
        if (startDate && endDate) {
          return templates.filter(template => {
            const templateDate = new Date(template.lastModified);
            return templateDate >= new Date(startDate) && templateDate <= new Date(endDate);
          });
        }
        return templates;
      default:
        return templates;
    }
    
    return templates.filter(template => {
      const templateDate = new Date(template.lastModified);
      return templateDate >= startDateObj;
    });
  };

  const filteredPerformanceTemplates = getFilteredTemplates();
  
  // Calculate performance metrics
  const totalTemplates = filteredPerformanceTemplates.length;
  const totalUsage = filteredPerformanceTemplates.reduce((sum, template) => sum + template.usageCount, 0);
  const avgResponseRate = 12.4; // This would be calculated from actual data in a real app
  const bestPerformer = filteredPerformanceTemplates.reduce((best, current) => 
    current.usageCount > best.usageCount ? current : best, filteredPerformanceTemplates[0] || templates[0]);

  // Top performing templates
  const topPerformers = [...filteredPerformanceTemplates]
    .sort((a, b) => b.usageCount - a.usageCount)
    .slice(0, 3);

  // Handle export dropdown click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setIsExportDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDeleteTemplate = (template: Template) => {
    setTemplateToDelete(template);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteTemplate = () => {
    if (templateToDelete) {
      deleteTemplate(templateToDelete.id);
      setIsDeleteModalOpen(false);
      setTemplateToDelete(null);
    }
  };

  const cancelDeleteTemplate = () => {
    setIsDeleteModalOpen(false);
    setTemplateToDelete(null);
  };

  const openEditModal = (template: Template) => {
    setEditingTemplate(template);
    setEditedContent(template.content);
    setCharCount(template.content.length);
    setSmsCount(Math.ceil(template.content.length / 160));
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingTemplate(null);
    setEditedContent('');
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const content = e.target.value;
    setEditedContent(content);
    setCharCount(content.length);
    setSmsCount(Math.ceil(content.length / 160));
  };

  const handleUpdateTemplate = () => {
    if (editingTemplate) {
      updateTemplate(editingTemplate.id, { content: editedContent });
      closeEditModal();
    }
  };

  const insertVariable = (variable: string) => {
    if (contentRef.current) {
      const textarea = contentRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = editedContent;
      const before = text.substring(0, start);
      const after = text.substring(end);
      
      setEditedContent(before + variable + after);
      
      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  const handleFilterChange = (filter: string) => {
    setPerformanceFilter(filter);
    if (filter === 'custom') {
      setCustomDateRange(true);
    } else {
      setCustomDateRange(false);
      setStartDate('');
      setEndDate('');
    }
  };

  const exportData = (format: string) => {
    // In a real application, this would generate and download the file
    // For now, we'll just log the action
    console.log(`Exporting data as ${format}`);
    
    // Example implementation for different formats
    switch (format) {
      case 'pdf':
        // Generate PDF
        alert('PDF export functionality would be implemented here');
        break;
      case 'csv':
        // Generate CSV
        alert('CSV export functionality would be implemented here');
        break;
      case 'update':
        // Update data
        alert('Data update functionality would be implemented here');
        break;
      default:
        break;
    }
    
    setIsExportDropdownOpen(false);
  };

  // Variables for template
  const variables = [
    { name: 'Customer Name', value: '{name}' },
    { name: 'Current Date', value: '{date}' },
    { name: 'Current Time', value: '{time}' },
    { name: 'Order Number', value: '{order_number}' },
    { name: 'Appointment Date', value: '{appointment_date}' },
    { name: 'Appointment Time', value: '{appointment_time}' },
    { name: 'Company Name', value: '{company_name}' },
    { name: 'Customer Email', value: '{email}' },
    { name: 'Customer Phone', value: '{phone}' },
    { name: 'Custom Variable', value: '{custom_variable}' },
  ];

  return (
    <div>
      <h1 className="text-2xl font-medium mb-6">Template Management</h1>

      {/* Section 1: Create and Edit Templates */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">Template Library</h2>
          <button 
            onClick={() => navigate('/superadmin/templates/create')}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
          >
            Create Template
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search templates..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Template Categories */}
        <div className="flex space-x-4 mb-6">
          <button 
            className={`px-4 py-2 rounded-md ${
              selectedCategory === 'all' 
                ? 'bg-[#FF5722] text-white' 
                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setSelectedCategory('all')}
          >
            All
          </button>
          {['Marketing', 'Notifications', 'Reminders', 'Custom'].map((category) => (
            <button
              key={category}
              className={`px-4 py-2 rounded-md ${
                selectedCategory === category 
                  ? 'bg-[#FF5722] text-white' 
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.length > 0 ? (
            filteredTemplates.map((template) => (
              <div key={template.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div>
                    <h3 className="font-medium">{template.name}</h3>
                    <p className="text-sm text-gray-600">{template.category}</p>
                </div>
                <div className="flex space-x-2">
                    <button 
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => openEditModal(template)}
                    >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                    <button 
                      className="text-red-600 hover:text-red-800"
                      onClick={() => handleDeleteTemplate(template)}
                    >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {template.content}
              </p>
              <div className="flex justify-between items-center text-sm text-gray-500">
                  <span>Last modified: {formatDistanceToNow(template.lastModified)} ago</span>
                  <span>Used: {template.usageCount} times</span>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-gray-500">
              No templates found. {searchTerm ? 'Try a different search term.' : 'Create a new template to get started.'}
            </div>
          )}
        </div>
      </div>

      {/* Section 2: Template Performance Tracking */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">Template Performance</h2>
          <div className="flex items-center space-x-4">
            {/* Performance Filter */}
            <div className="relative">
              <select
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                value={performanceFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="year">Last Year</option>
                <option value="custom">Custom Range</option>
              </select>
              
              {/* Custom Date Range */}
              {customDateRange && (
                <div className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-md shadow-lg p-4 z-10 w-64">
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      className="px-3 py-1 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors text-sm"
                      onClick={() => setCustomDateRange(false)}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Export Button */}
            <div className="relative" ref={exportDropdownRef}>
              <button
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center"
                onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export Data
              </button>
              
              {/* Export Dropdown */}
              {isExportDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white border border-gray-300 rounded-md shadow-lg z-10 w-48">
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center"
                    onClick={() => exportData('pdf')}
                  >
                    <svg className="w-5 h-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                    Export as PDF
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center"
                    onClick={() => exportData('csv')}
                  >
                    <svg className="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                    Export as CSV
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center"
                    onClick={() => exportData('update')}
                  >
                    <svg className="w-5 h-5 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                    Update Data
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Performance Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Total Templates</h3>
            <div className="text-2xl font-bold text-[#FF5722]">{totalTemplates}</div>
            <p className="text-sm text-gray-600">Active templates</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Total Usage</h3>
            <div className="text-2xl font-bold text-blue-600">{totalUsage}</div>
            <p className="text-sm text-gray-600">Messages sent</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Avg. Response Rate</h3>
            <div className="text-2xl font-bold text-green-600">{avgResponseRate}%</div>
            <p className="text-sm text-gray-600">Last 30 days</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Best Performer</h3>
            <div className="text-lg font-bold text-[#FF5722] truncate">{bestPerformer?.name || 'N/A'}</div>
            <p className="text-sm text-gray-600">{bestPerformer ? `${bestPerformer.usageCount} uses` : 'No data'}</p>
          </div>
        </div>

        {/* Top Performing Templates */}
        <div className="border rounded-lg p-4">
          <h3 className="font-medium mb-4">Top Performing Templates</h3>
          <div className="space-y-4">
            {topPerformers.map((template, index) => (
              <div key={template.id} className="p-3 bg-gray-50 rounded">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium">{template.name}</p>
                    <p className="text-sm text-gray-600">Used {template.usageCount} times</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-green-600">{((template.usageCount / totalUsage) * 100).toFixed(1)}%</p>
                    <p className="text-sm text-gray-600">Of total usage</p>
                  </div>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div 
                    className="h-2 bg-[#FF5722] rounded-full" 
                    style={{ width: `${(template.usageCount / totalUsage) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Edit Template Modal */}
      {isEditModalOpen && editingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-medium">Edit Template: {editingTemplate.name}</h2>
                <button 
                  onClick={closeEditModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Template Content
                  </label>
                  <select 
                    className="text-sm px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    onChange={(e) => {
                      if (e.target.value) {
                        insertVariable(e.target.value);
                        e.target.value = ''; // Reset the dropdown
                      }
                    }}
                  >
                    <option value="">Insert Variable</option>
                    {variables.map((variable) => (
                      <option key={variable.value} value={variable.value}>
                        {variable.name} ({variable.value})
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  ref={contentRef}
                  value={editedContent}
                  onChange={handleContentChange}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  placeholder="Enter your template content. Use variables for dynamic content."
                />
                
                {/* Character Counter */}
                <div className="mt-2 flex justify-between items-center text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600">Characters:</span>
                    <span className={`font-medium ${charCount > 160 ? 'text-red-500' : 'text-gray-900'}`}>
                      {charCount}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600">SMS Count:</span>
                    <span className={`font-medium ${smsCount > 1 ? 'text-amber-500' : 'text-gray-900'}`}>
                      {smsCount}
                    </span>
                  </div>
                </div>
              </div>

              {/* Variable Help Text */}
              <div className="bg-gray-50 p-4 rounded-md mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Available Variables</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {variables.map((variable) => (
                    <div key={variable.value} className="flex items-center">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm mr-2">{variable.value}</code>
                      <span className="text-sm text-gray-600">{variable.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors"
                  onClick={handleUpdateTemplate}
                >
                  Update Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Template Confirmation Modal */}
      {isDeleteModalOpen && templateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-medium">Delete Template</h2>
                <button 
                  onClick={cancelDeleteTemplate}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700 mb-2">
                  Are you sure you want to delete the template <span className="font-medium">"{templateToDelete.name}"</span>?
                </p>
                <p className="text-sm text-gray-600">
                  This action cannot be undone. The template will be permanently removed from the library.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-md mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Template Content</h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {templateToDelete.content}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={cancelDeleteTemplate}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  onClick={confirmDeleteTemplate}
                >
                  Delete Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManagement; 